import { userManager } from '../auth/AuthProvider';
import type { AppError } from '../notifications/NotificationProvider';

const API_URL = import.meta.env.VITE_API_URL as string;

async function authHeader(): Promise<Record<string, string>> {
  const user = await userManager.getUser();
  if (!user || user.expired) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${user.access_token}` };
}

/**
 * Thrown for any non-2xx response. Extends Error so it works with TanStack Query
 * error handling, but carries the structured fields the NotificationProvider
 * expects so toasts render with troubleshooting detail.
 */
export class ApiError extends Error implements AppError {
  code?: string;
  requestId?: string;
  timestamp?: string;
  status?: number;
  fields?: Record<string, string>;

  constructor(init: AppError) {
    super(init.message);
    this.name = 'ApiError';
    Object.assign(this, init);
  }
}

async function parseError(res: Response): Promise<ApiError> {
  // Prefer the request ID from headers — it's set even when the body fails to
  // parse (e.g. CloudFront/WAF blocks before reaching the API).
  const headerRequestId =
    res.headers.get('x-request-id') ?? res.headers.get('x-amzn-RequestId') ?? undefined;
  let body: { error?: AppError } | undefined;
  try {
    body = await res.json();
  } catch {
    // non-JSON response (HTML error page from a proxy, etc.)
  }
  const fromBody = body?.error;
  const requestId = fromBody?.requestId ?? headerRequestId;
  return new ApiError({
    ...(fromBody?.code !== undefined && { code: fromBody.code }),
    message: (fromBody?.message ?? `${res.status} ${res.statusText}`) || 'Request failed',
    ...(requestId !== undefined && { requestId }),
    timestamp: fromBody?.timestamp ?? new Date().toISOString(),
    status: fromBody?.status ?? res.status,
    ...(fromBody?.fields !== undefined && { fields: fromBody.fields }),
  });
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeader()),
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    // Network-level failure (offline, DNS, CORS preflight rejection)
    throw new ApiError({
      code: 'NETWORK_ERROR',
      message: cause instanceof Error ? cause.message : 'Network request failed',
      timestamp: new Date().toISOString(),
    });
  }

  if (!res.ok) throw await parseError(res);
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const apiClient = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  put: <T>(p: string, b?: unknown) => request<T>('PUT', p, b),
  patch: <T>(p: string, b?: unknown) => request<T>('PATCH', p, b),
  del: <T>(p: string) => request<T>('DELETE', p),
};
